import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import textStyles from 'config/branding/textStyles';
import { styled, useStyletron } from '@activebrands/core-web/libs/styletron';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
import responsiveTextStyles from '@activebrands/core-web/utils/responsive-text-styles';
import InputErrors from 'components/Form/InputErrors';

export const Label = styled('label', ({ $required }) => ({
    color: 'var(--color-text-subtle)',
    marginLeft: '6px',
    flexShrink: 0,
    ...textStyles['Miscellaneous/12_100'],

    ':after': {
        content: $required ? '"*"' : '""',
    },
}));

const LABEL_BAND = '28px';
const INLINE_ERROR_LINE_HEIGHT = '12px';

const LabelWrapperRoot = styled('div', ({ $style: wrapperStyle = {} }) => ({
    position: 'relative',
    width: '100%',
    minHeight: LABEL_BAND,
    marginBottom: '4px',
    ...wrapperStyle,
}));

const LabelWrapperRow = styled('div', {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    maxHeight: LABEL_BAND,
    overflow: 'hidden',
});

export const LabelWrapper = ({ $style, children }) => (
    <LabelWrapperRoot $style={$style}>
        <LabelWrapperRow>{children}</LabelWrapperRow>
    </LabelWrapperRoot>
);

export const ErrorWrapper = styled('div', ({ $style: wrapperStyle = {} }) => ({
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
    justifyContent: 'flex-end',
    maxHeight: LABEL_BAND,
    overflow: 'hidden',
    ...wrapperStyle,
}));

LabelWrapper.propTypes = {
    $style: PropTypes.object,
    children: PropTypes.node,
};

const StyledInput = styled('input', ({ $errors }) => ({
    width: '100%',
    padding: '8px',
    border: $errors && $errors.length ? '1px solid var(--color-text-error)' : '1px solid var(--color-border-subtle)',
    borderRadius: '8px',
    background: 'var(--color-bg-highlight)',
    color: 'var(--color-text)',
    ...textStyles['Miscellaneous/18_100_-1'],

    '::placeholder': {
        color: 'var(--color-text-subtle)',
    },

    ':-webkit-autofill': {
        boxShadow: `0 0 0 30px var(--color-bg) inset !important`,
        ':hover': {
            boxShadow: `0 0 0 30px var(--color-bg) inset !important`,
        },
        ':focus': {
            boxShadow: `0 0 0 30px var(--color-bg) inset !important`,
        },
    },

    ':read-only': {
        color: 'var(--color-text)',
        cursor: `url("data:image/svg+xml,%0A%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M14 9v2h-4v-2c0-1.104.897-2 2-2s2 .896 2 2zm10 3c0 6.627-5.373 12-12 12s-12-5.373-12-12 5.373-12 12-12 12 5.373 12 12zm-8-1h-1v-2c0-1.656-1.343-3-3-3s-3 1.344-3 3v2h-1v6h8v-6z'/%3E%3C/svg%3E"), auto`,
    },

    ...getFocusRingStyles(),
}));

const Description = styled('span', ({ $warning }) => ({
    ...textStyles['Miscellaneous/12_100'],
    display: 'block',
    margin: '4px 6px',
    color: $warning ? 'var(--color-text-error)' : 'var(--color-contrast-high)',
    userSelect: 'none',
}));

/**
 * @param {object} [$style] - Optional styling to input.
 * @param {boolean} [required=false] - default value. Sets if input field is required.
 */
const Input = ({
    $style = {},
    ariaLabel = '',
    dataLocale = '',
    defaultValue = '',
    description,
    errorMessageStyle = {},
    errors = [],
    handleChange = () => null,
    inputStyle = {},
    label,
    labelStyle,
    labelWrapperStyle,
    inputFontKeys = 'Miscellaneous/18_100_-1',
    name,
    onBlur = () => null,
    onChange = () => null,
    placeholder,
    required = false,
    selector,
    type = 'text',
    validate = '',
    value = '',
    ...rest
}) => {
    const [css] = useStyletron();
    const [inputValue, setInputValue] = useState(defaultValue);
    const inputFontStyle = responsiveTextStyles(inputFontKeys);

    const handleKeyDown = e => {
        setInputValue(e.currentTarget.value);
        handleChange(e.currentTarget.value);
        onChange(e);
    };

    const handleBlur = e => {
        setInputValue(e.currentTarget.value.trim());
        onBlur(e);
    };

    useEffect(() => {
        if (value !== '' && inputValue !== value) {
            setInputValue(value);
        }
    }, [value]);

    useEffect(() => {
        if ((defaultValue !== '' && inputValue !== defaultValue) || defaultValue === '') {
            setInputValue(defaultValue);
        }
    }, [defaultValue]);

    const hasErrors = errors && errors.length > 0;
    const errorId = `${name}-error`;

    return (
        <div
            className={css({
                position: 'relative',
                marginTop: '12px',
                ...$style,
                display: type === 'hidden' ? 'none' : null,
            })}
            data-selector={selector}
        >
            {(label || hasErrors) && (
                <LabelWrapper $style={labelWrapperStyle}>
                    {label && (
                        <Label $required={required} $style={labelStyle} htmlFor={name}>
                            {label}
                        </Label>
                    )}
                    {hasErrors && (
                        <ErrorWrapper>
                            <InputErrors
                                id={errorId}
                                $style={{
                                    textAlign: 'right',
                                    margin: 0,
                                    lineHeight: INLINE_ERROR_LINE_HEIGHT,
                                    ...errorMessageStyle,
                                }}
                                errors={errors}
                            />
                        </ErrorWrapper>
                    )}
                </LabelWrapper>
            )}
            <StyledInput
                $style={{ ...inputFontStyle, ...inputStyle }}
                $errors={errors}
                id={name}
                aria-label={ariaLabel || undefined}
                aria-invalid={hasErrors ? 'true' : undefined}
                aria-describedby={hasErrors ? errorId : undefined}
                data-locale={dataLocale || null}
                data-validate={validate || null}
                name={name}
                placeholder={placeholder}
                required={required}
                type={type}
                value={inputValue}
                onBlur={handleBlur}
                onChange={handleKeyDown}
                {...rest}
            />
            {description && <Description>{description}</Description>}
        </div>
    );
};

Input.propTypes = {
    $style: PropTypes.object,
    ariaLabel: PropTypes.string,
    dataLocale: PropTypes.string,
    defaultValue: PropTypes.string,
    description: PropTypes.string,
    errorMessageStyle: PropTypes.object,
    errors: PropTypes.array,
    handleChange: PropTypes.func,
    inputFontKeys: PropTypes.string,
    inputStyle: PropTypes.object,
    label: PropTypes.string,
    labelStyle: PropTypes.object,
    labelWrapperStyle: PropTypes.object,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    selector: PropTypes.string,
    type: PropTypes.oneOf(['text', 'tel', 'email', 'number', 'password', 'numeric']),
    validate: PropTypes.string,
    value: PropTypes.string,
};

export default Input;
